import { AxiosHeaders } from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import config from 'utils/config';
import logProviderFactory from 'utils/logger/logProviderFactory';

import type { AwsWafClientConfig } from './awsWafConfig';
import { awsWafDomainMatchesHostname, getAwsWafClientConfig } from './awsWafConfig';

const AWS_WAF_HEADER_NAME = 'x-aws-waf-token';
const AWS_WAF_SCRIPT_ID = 'aws-waf-sdk';
const AWS_WAF_DEFAULT_TOKEN_TIMEOUT_MS = 8000;
const AWS_WAF_SCRIPT_LOAD_TIMEOUT_MS = 8000;
const AWS_WAF_SCRIPT_LOAD_RETRY_COOLDOWN_MS = 30000;

declare global {
    interface Window {
        AwsWafIntegration?: {
            getToken: () => Promise<string>;
            hasToken?: () => boolean;
            forceStartBackgroundRefresh?: () => void;
        };
        awsWafCookieDomainList?: string[];
        awsWafFetchRetryCount?: number;
        awsWafMaxTokenAge?: number;
        __awsWafPortalLoadPromise?: Promise<void>;
        __awsWafPortalRefreshStarted?: boolean;
    }
}

let cachedRuntimeConfig: AwsWafClientConfig | null = null;
let runtimeConfigApplied = false;
let nextScriptLoadAttemptAt = 0;

type AwsWafScriptStatus = 'loading' | 'loaded' | 'error';

const getRuntimeConfig = (): AwsWafClientConfig => {
    if (!cachedRuntimeConfig) {
        cachedRuntimeConfig = getAwsWafClientConfig({
            baseUrl: config.baseUrl,
            exchangeApiUrl: config.exchangeApiUrl,
            integrationUrl: config.awsWafIntegrationUrl,
            cookieDomainList: config.awsWafCookieDomainList,
            fetchRetryCount: config.awsWafFetchRetryCount,
            maxTokenAgeMs: config.awsWafMaxTokenAgeMs,
            currentHostname: typeof window !== 'undefined' ? window.location.hostname : null,
        });
    }

    return cachedRuntimeConfig;
};

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
    let timeoutId: number | undefined;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeoutId = window.setTimeout(
                    () => reject(new Error(`${label} timed out after ${timeoutMs}ms`)),
                    timeoutMs,
                );
            }),
        ]);
    } finally {
        if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
        }
    }
};

const applyWindowConfiguration = (): string | null => {
    if (typeof window === 'undefined') return null;

    const runtimeConfig = getRuntimeConfig();
    if (!runtimeConfig.integrationUrl) return null;
    if (runtimeConfigApplied) return runtimeConfig.integrationUrl;

    if (runtimeConfig.cookieDomains.length > 0) {
        window.awsWafCookieDomainList = runtimeConfig.cookieDomains;
    }
    if (runtimeConfig.fetchRetryCount != null) {
        window.awsWafFetchRetryCount = runtimeConfig.fetchRetryCount;
    }
    if (runtimeConfig.maxTokenAgeMs != null) {
        window.awsWafMaxTokenAge = runtimeConfig.maxTokenAgeMs;
    }
    runtimeConfigApplied = true;

    return runtimeConfig.integrationUrl;
};

const startBackgroundRefresh = () => {
    if (typeof window === 'undefined' || !window.AwsWafIntegration || window.__awsWafPortalRefreshStarted) {
        return;
    }

    window.AwsWafIntegration.forceStartBackgroundRefresh?.();
    window.__awsWafPortalRefreshStarted = true;
};

const getScriptStatus = (script: HTMLScriptElement): AwsWafScriptStatus | null => {
    const value = script.dataset.awsWafStatus;
    if (value === 'loading' || value === 'loaded' || value === 'error') {
        return value;
    }
    return null;
};

const setScriptStatus = (script: HTMLScriptElement, status: AwsWafScriptStatus) => {
    script.dataset.awsWafStatus = status;
};

const noteScriptLoadFailure = () => {
    nextScriptLoadAttemptAt = Date.now() + AWS_WAF_SCRIPT_LOAD_RETRY_COOLDOWN_MS;
};

const clearScriptLoadFailure = () => {
    nextScriptLoadAttemptAt = 0;
};

const shouldSkipScriptLoadAttempt = () => nextScriptLoadAttemptAt > 0 && Date.now() < nextScriptLoadAttemptAt;

export const shouldBypassAwsWafForHost = (currentHostname: string, runtimeConfig: AwsWafClientConfig): boolean => {
    const normalizedHostname = currentHostname.toLowerCase();
    if (normalizedHostname === 'localhost' || normalizedHostname === '127.0.0.1') {
        return true;
    }

    if (!normalizedHostname.endsWith('.vercel.app')) {
        return false;
    }

    return !runtimeConfig.cookieDomains.some((domain) => awsWafDomainMatchesHostname(normalizedHostname, domain));
};

const shouldBypassAwsWafForCurrentHost = (runtimeConfig: AwsWafClientConfig): boolean => {
    if (typeof window === 'undefined') return false;

    const currentHostname = window.location.hostname.toLowerCase();
    return shouldBypassAwsWafForHost(currentHostname, runtimeConfig);
};

const resolveRequestUrl = (url?: string, baseURL?: string): URL | null => {
    // AWS WAF's challenge/token SDK runs in the browser only. Server-side callers
    // intentionally fall back to plain fetch/axios semantics.
    if (typeof window === 'undefined' || !url) return null;

    try {
        return new URL(url, baseURL ?? window.location.origin);
    } catch {
        return null;
    }
};

const isProtectedApiRequest = (url?: string, baseURL?: string): boolean => {
    const requestUrl = resolveRequestUrl(url, baseURL);
    if (!requestUrl) return false;

    const protectedHost = resolveRequestUrl(config.exchangeApiUrl)?.hostname;
    return !!protectedHost && requestUrl.hostname === protectedHost;
};

const isProtectedFetchRequest = (input: Parameters<typeof fetch>[0]): boolean => {
    if (typeof window === 'undefined') return false;

    if (typeof input === 'string') {
        return isProtectedApiRequest(input);
    }
    if (input instanceof URL) {
        return isProtectedApiRequest(input.toString());
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return isProtectedApiRequest(input.url);
    }

    return false;
};

const withAwsWafHeader = (
    input: Parameters<typeof fetch>[0],
    init: Parameters<typeof fetch>[1] | undefined,
    token: string,
): Parameters<typeof fetch>[1] => {
    const headers =
        typeof Request !== 'undefined' && input instanceof Request ? new Headers(input.headers) : new Headers();

    new Headers(init?.headers).forEach((value, key) => {
        headers.set(key, value);
    });
    headers.set(AWS_WAF_HEADER_NAME, token);
    return {
        ...(init ?? {}),
        headers,
    };
};

const executeFetch = (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]): Promise<Response> => {
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return fetch(new Request(input, init));
    }

    return fetch(input, init);
};

type AwsWafRetryableConfig = InternalAxiosRequestConfig & {
    _awsWafRetried?: boolean;
};

const isAbortError = (error: unknown): boolean =>
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError');

const isRetryableFetchError = (error: unknown): boolean => {
    if (isAbortError(error)) {
        return false;
    }

    if (error instanceof TypeError) {
        return true;
    }

    return error instanceof Error && error.name === 'NetworkError';
};

export const ensureAwsWafReady = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;

    const runtimeConfig = getRuntimeConfig();
    if (shouldBypassAwsWafForCurrentHost(runtimeConfig)) {
        return false;
    }

    const integrationUrl = applyWindowConfiguration();
    if (!integrationUrl) return false;

    if (window.AwsWafIntegration) {
        clearScriptLoadFailure();
        startBackgroundRefresh();
        return true;
    }

    if (shouldSkipScriptLoadAttempt()) {
        return false;
    }

    if (window.__awsWafPortalLoadPromise) {
        try {
            await window.__awsWafPortalLoadPromise;
            clearScriptLoadFailure();
            startBackgroundRefresh();
            return !!window.AwsWafIntegration;
        } catch {
            noteScriptLoadFailure();
            return false;
        }
    }

    const existingScript = document.getElementById(AWS_WAF_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
        const status = getScriptStatus(existingScript);
        if (status === 'loaded') {
            clearScriptLoadFailure();
            return !!window.AwsWafIntegration;
        }
        if (status === 'error') {
            noteScriptLoadFailure();
            return false;
        }
    }

    window.__awsWafPortalLoadPromise = withTimeout(
        new Promise<void>((resolve, reject) => {
            const script = existingScript ?? document.createElement('script');
            script.id = AWS_WAF_SCRIPT_ID;
            script.async = true;
            script.src = `${integrationUrl}challenge.js`;

            const handleLoad = () => {
                setScriptStatus(script, 'loaded');
                resolve();
            };
            const handleError = () => {
                setScriptStatus(script, 'error');
                reject(new Error('Failed to load AWS WAF challenge.js'));
            };

            script.addEventListener('load', handleLoad, { once: true });
            script.addEventListener('error', handleError, { once: true });

            if (!existingScript) {
                setScriptStatus(script, 'loading');
                document.head.appendChild(script);
            }
        }),
        AWS_WAF_SCRIPT_LOAD_TIMEOUT_MS,
        'AWS WAF script load',
    );

    try {
        await window.__awsWafPortalLoadPromise;
        clearScriptLoadFailure();
        startBackgroundRefresh();
        return !!window.AwsWafIntegration;
    } catch {
        noteScriptLoadFailure();
        return false;
    } finally {
        window.__awsWafPortalLoadPromise = undefined;
    }
};

export const getAwsWafToken = async (timeoutMs = AWS_WAF_DEFAULT_TOKEN_TIMEOUT_MS): Promise<string | null> => {
    try {
        if (!(await ensureAwsWafReady()) || !window.AwsWafIntegration) {
            return null;
        }

        const token = await withTimeout(window.AwsWafIntegration.getToken(), timeoutMs, 'AWS WAF token fetch');
        startBackgroundRefresh();
        return token || null;
    } catch (error) {
        logProviderFactory.logWarning(
            `AWS WAF token fetch failed${error instanceof Error ? `: ${error.message}` : ''}`,
        );
        return null;
    }
};

export const attachAwsWafTokenToAxiosConfig = async <T extends InternalAxiosRequestConfig>(
    requestConfig: T,
): Promise<T> => {
    if (!isProtectedApiRequest(requestConfig.url, requestConfig.baseURL)) {
        return requestConfig;
    }

    const token = await getAwsWafToken();
    if (!token) {
        return requestConfig;
    }

    requestConfig.headers = AxiosHeaders.from(requestConfig.headers);
    requestConfig.headers.set(AWS_WAF_HEADER_NAME, token);
    return requestConfig;
};

export const maybeRetryAxiosRequestWithAwsWafToken = async (
    client: AxiosInstance,
    error: AxiosError,
): Promise<unknown> => {
    const requestConfig = error.config as AwsWafRetryableConfig | undefined;
    if (!requestConfig || requestConfig._awsWafRetried || error.response || error.code !== 'ERR_NETWORK') {
        throw error;
    }

    if (!isProtectedApiRequest(requestConfig.url, requestConfig.baseURL)) {
        throw error;
    }

    const token = await getAwsWafToken();
    if (!token) {
        throw error;
    }

    requestConfig._awsWafRetried = true;
    requestConfig.headers = AxiosHeaders.from(requestConfig.headers);
    requestConfig.headers.set(AWS_WAF_HEADER_NAME, token);
    return client.request(requestConfig);
};

export const ensureAwsWafTokenForWebSocket = async (wsUrl?: string): Promise<void> => {
    if (typeof window === 'undefined' || !wsUrl) return;

    const protectedHost = resolveRequestUrl(config.exchangeWsUrl)?.hostname;
    const targetHost = resolveRequestUrl(wsUrl)?.hostname;
    if (!protectedHost || !targetHost || protectedHost !== targetHost) {
        return;
    }

    await getAwsWafToken();
};

export const fetchWithAwsWaf = async (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
): Promise<Response> => {
    if (!isProtectedFetchRequest(input)) {
        return executeFetch(input, init);
    }

    const token = await getAwsWafToken();
    const firstInit = token ? withAwsWafHeader(input, init, token) : init;

    try {
        return await executeFetch(input, firstInit);
    } catch (error) {
        if (!isRetryableFetchError(error)) {
            throw error;
        }

        const refreshedToken = await getAwsWafToken();
        if (!refreshedToken || refreshedToken === token) {
            throw error;
        }

        return executeFetch(input, withAwsWafHeader(input, init, refreshedToken));
    }
};

export const __resetAwsWafForTesting = (): void => {
    cachedRuntimeConfig = null;
    runtimeConfigApplied = false;
    nextScriptLoadAttemptAt = 0;
    if (typeof window !== 'undefined') {
        window.__awsWafPortalLoadPromise = undefined;
        window.__awsWafPortalRefreshStarted = false;
    }
};
